import React, { useMemo, useCallback, ChangeEvent, useState } from 'react';
import { DataGrid, GridColumns } from '@material-ui/data-grid';
import {
  Row,
  Col,
  OverlayTrigger,
  Tooltip as TooltipBootstrap,
} from 'react-bootstrap';
import { GoPackage, GoPencil } from 'react-icons/go';
import { MdDeleteForever } from 'react-icons/md';
import { nanoid } from 'nanoid';
import { toast } from 'react-toastify';
import { useProduto } from '~/pages/Produto/hooks/useProduto';
import { ConfirmButton, ClearButton } from '~/components/Buttons';
import Separator from '~/components/Separator';
import ToggleDefault from '~/components/ToggleDefault';
import {
  InputText,
  InputSelect,
  InputNumber,
  BuscaParceiro,
  InputRadio,
} from '~/components/NovosInputs';
import { AcoesContainer, ButtonRow, TableContainer } from './styles';
import { SelectType } from '~/components/NovosInputs/InputSelect/protocols';
import { useProdutoQueries } from '../../hooks/useQueriesProduto';

const AbaFornecedor = () => {
  const {
    setValue,
    getValues,
    register,
    control,
    clearErrors,
    formState: { errors },
    watch,
    flags,
    ChangeFlags,
    selectedCodFornecedor,
    setSelectedCodFornecedor,
    setFormProdutoFornecedor,
    formProdutoFornecedor,
    handleClearProdutoFornecedor,
  } = useProduto();
  const { unidades: unRequest } = useProdutoQueries();
  const [editUnidadeId, setEditUnidadeId] = useState<string | null>(null);
  const fornecedores = getValues('fornecedores') || [];

  const handleClearProdFornUN = useCallback(() => {
    setValue('des_referencia', '');
    setValue('des_unidade_compra_fornecedor', {
      value: 'UN',
      label: 'UN - Unidade',
    });
    setValue('qtd_embalagem_compra_fornecedor', 1);
  }, [setValue]);

  const updateFornecedores = useCallback(
    (list) => {
      setValue('fornecedores', list, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue],
  );

  const getFornecedorById = (id: any) =>
    fornecedores.find(
      (f: any) => Number(f.cod_fornecedor.value) === Number(id),
    );

  /* ===================== Fornecedor ===================== */
  const handleAddFornecedor = () => {
    const exists = fornecedores.find(
      (f: any) =>
        Number(f.cod_fornecedor.value) ===
        Number(formProdutoFornecedor.cod_fornecedor.value),
    );

    if (exists) return toast.warning('Fornecedor já adicionado');

    const novo = {
      ...formProdutoFornecedor,
      flg_principal: {
        ...formProdutoFornecedor.flg_principal,
        value: fornecedores.length === 0,
      },
      flg_fabricante: {
        ...formProdutoFornecedor.flg_fabricante,
        value: flags.flg_fabricante,
      },
      Unidades: { unidades: [] },
    };

    updateFornecedores([...fornecedores, novo]);
    handleClearProdutoFornecedor();
  };

  const handleDeleteFornecedor = (cod: any) => {
    const target = getFornecedorById(cod);
    if (target?.flg_principal?.value) {
      return toast.warning(
        'Defina outro fornecedor como principal antes de excluir',
      );
    }

    const filtered = fornecedores.filter(
      (f: any) => Number(f.cod_fornecedor.value) !== Number(cod),
    );
    updateFornecedores(filtered);

    if (cod === selectedCodFornecedor) setSelectedCodFornecedor(undefined);
  };

  const handleSetPrincipal = (cod: any) => {
    const updated = fornecedores.map((f: any) => ({
      ...f,
      flg_principal: {
        ...f.flg_principal,
        value: Number(f.cod_fornecedor.value) === Number(cod),
      },
    }));

    updateFornecedores(updated);
  };

  const handleToggleFabricante = (cod: any) => {
    const updated = fornecedores.map((f: any) =>
      Number(f.cod_fornecedor.value) === Number(cod)
        ? {
            ...f,
            flg_fabricante: {
              ...f.flg_fabricante,
              value: !f.flg_fabricante.value,
            },
          }
        : f,
    );

    updateFornecedores(updated);
  };

  /* ===================== Embalagens ===================== */
  const handleAddUnidade = () => {
    if (!selectedCodFornecedor) return toast.warning('Selecione um fornecedor');
    const {
      des_referencia,
      des_unidade_compra_fornecedor,
      qtd_embalagem_compra_fornecedor,
    } = getValues();
    const fornecedor = getFornecedorById(selectedCodFornecedor);
    if (!fornecedor) return;

    if (!des_referencia) return toast.warning('Informe a referência');
    if (!des_unidade_compra_fornecedor.value)
      return toast.warning('Informe a unidade de compra');
    if (!qtd_embalagem_compra_fornecedor)
      return toast.warning('Informe a quantidade');

    const unidades = fornecedor.Unidades.unidades || [];

    if (editUnidadeId) {
      const novasUnidades = unidades.map((u: any) =>
        u.id.value === editUnidadeId
          ? {
              ...u,
              des_referencia,
              des_unidade_compra: des_unidade_compra_fornecedor.value,
              qtd_embalagem_compra: qtd_embalagem_compra_fornecedor,
            }
          : u,
      );

      const updated = fornecedores.map((f: any) =>
        Number(f.cod_fornecedor.value) === Number(selectedCodFornecedor)
          ? { ...f, Unidades: { unidades: novasUnidades } }
          : f,
      );

      setEditUnidadeId(null);
      updateFornecedores(updated);
      handleClearProdFornUN();
      return;
    }

    const exists = unidades.some(
      (u: any) =>
        String(u.des_referencia).trim().toLowerCase() ===
        String(des_referencia).trim().toLowerCase(),
    );

    if (exists) return toast.warning('Referência já cadastrada');

    const novaUn = {
      id: { value: nanoid() },
      des_referencia,
      des_unidade_compra: des_unidade_compra_fornecedor.value,
      qtd_embalagem_compra: qtd_embalagem_compra_fornecedor,
    };

    const novasUnidades = [...unidades, novaUn];

    const updated = fornecedores.map((f: any) =>
      Number(f.cod_fornecedor.value) === Number(selectedCodFornecedor)
        ? { ...f, Unidades: { unidades: novasUnidades } }
        : f,
    );

    updateFornecedores(updated);
    handleClearProdFornUN();
  };

  const handleEditUnidade = (id: any) => {
    if (!selectedCodFornecedor) return;

    const fornecedor = getFornecedorById(selectedCodFornecedor);
    if (!fornecedor) return;

    const unidade = fornecedor.Unidades.unidades.find(
      (u: any) => u.id.value === id,
    );

    if (!unidade) return toast.warning('Embalagem não encontrada');

    // Preenche os inputs
    setValue('des_referencia', unidade.des_referencia);
    setValue('des_unidade_compra_fornecedor', {
      label: unidade.des_unidade_compra,
      value: unidade.des_unidade_compra,
    });
    setValue('qtd_embalagem_compra_fornecedor', unidade.qtd_embalagem_compra);

    setEditUnidadeId(id);
  };

  const handleDeleteUnidade = (id: any) => {
    const fornecedor = getFornecedorById(selectedCodFornecedor);
    if (!fornecedor) return;

    const novasUnidades = fornecedor.Unidades.unidades.filter(
      (u: any) => u.id.value !== id,
    );

    const updated = fornecedores.map((f: any) =>
      Number(f.cod_fornecedor.value) === Number(selectedCodFornecedor)
        ? { ...f, Unidades: { unidades: novasUnidades } }
        : f,
    );

    updateFornecedores(updated);
  };

  const unidadesDoFornecedorSelecionado = useMemo(() => {
    if (!selectedCodFornecedor) return [];

    const fornecedor = getFornecedorById(selectedCodFornecedor);
    if (!fornecedor) return [];

    return (fornecedor.Unidades?.unidades || []).map((u: any) => ({
      id: u.id.value,
      des_referencia: u.des_referencia,
      des_unidade_compra: u.des_unidade_compra,
      qtd_embalagem_compra: u.qtd_embalagem_compra,
    }));
  }, [fornecedores, selectedCodFornecedor]);

  /* ===================== Columns ===================== */
  const fornecedorColumns: GridColumns = useMemo(
    () => [
      {
        field: 'num_cpf_cnpj',
        headerName: 'CNPJ',
        width: 150,
        sortable: false,
      },
      {
        field: 'des_fornecedor',
        headerName: 'Fornecedor',
        width: 150,
        flex: 1,
        sortable: false,
      },
      {
        field: 'flg_principal',
        headerName: 'Principal',
        sortable: false,
        width: 150,
        renderCell: ({ row }: any) => (
          <InputRadio
            register={register}
            isError={false}
            value="default radio"
            name="radio-default"
            checked={row.flg_principal}
            onChange={() => handleSetPrincipal(row.cod_fornecedor)}
          />
        ),
      },
      {
        field: 'flg_fabricante',
        headerName: 'Fabricante',
        sortable: false,
        width: 150,
        renderCell: ({ row }: any) => (
          <input
            type="checkbox"
            checked={row.flg_fabricante}
            onChange={() => handleToggleFabricante(row.cod_fornecedor)}
            style={{ accentColor: '#0052cc', cursor: 'pointer' }}
          />
        ),
      },
      {
        field: 'referencias',
        headerName: 'Referências',
        sortable: false,
        width: 150,
        renderCell: ({ row }: any) => {
          const arrRefs = row.unidades.map((item: any) => {
            return {
              id: item.id.value,
              ref: item.des_referencia,
              descricao: `${item.des_unidade_compra} C/${item.qtd_embalagem_compra} Ref: (${item.des_referencia})`,
            };
          });
          const refs = arrRefs
            .map((item: any) => {
              return item.ref;
            })
            .join(' | ');
          const descricao = arrRefs
            .map((item: any) => {
              return item.descricao;
            })
            .join('\n');
          return (
            <>
              <OverlayTrigger
                placement="bottom"
                overlay={<TooltipBootstrap id="">{descricao}</TooltipBootstrap>}
              >
                <p
                  style={{
                    margin: '0',
                  }}
                >
                  {refs}
                </p>
              </OverlayTrigger>
            </>
          );
        },
      },
      {
        field: 'acoes',
        headerName: 'Ações',
        headerAlign: 'center',
        width: 200,
        sortable: false,
        orderable: false,
        renderCell: ({ row }: any) => (
          <AcoesContainer>
            <div className="d-flex align-items-center gap-2">
              <ButtonRow
                type="button"
                onClick={() => setSelectedCodFornecedor(row.cod_fornecedor)}
              >
                <GoPackage size={20} style={{ color: '#979320' }} /> Embalagens
              </ButtonRow>
              <ButtonRow
                type="button"
                onClick={() => handleDeleteFornecedor(row.cod_fornecedor)}
              >
                <MdDeleteForever size={20} style={{ color: '#e63c3c' }} />{' '}
              </ButtonRow>
            </div>
          </AcoesContainer>
        ),
      },
    ],
    [fornecedores],
  );

  const unidadeColumns: GridColumns = useMemo(
    () => [
      {
        field: 'des_referencia',
        headerName: 'Referência',
        flex: 1,
        orderable: false,
        sortable: false,
      },
      {
        field: 'des_unidade_compra',
        headerName: 'Un Compra',
        flex: 1,
        sortable: false,
      },
      {
        field: 'qtd_embalagem_compra',
        headerName: 'Qtd',
        width: 150,
        sortable: false,
      },
      {
        field: 'acoes',
        headerAlign: 'center',
        sortable: false,
        headerName: 'Ações',
        width: 200,
        renderCell: ({ row }: any) => (
          <AcoesContainer>
            <ButtonRow type="button" onClick={() => handleEditUnidade(row.id)}>
              <GoPencil size={20} style={{ color: '#72ab90' }} /> Editar
            </ButtonRow>
            <ButtonRow
              type="button"
              onClick={() => handleDeleteUnidade(row.id)}
            >
              <MdDeleteForever size={20} style={{ color: '#e63c3c' }} /> Excluir
            </ButtonRow>
          </AcoesContainer>
        ),
      },
    ],
    [selectedCodFornecedor, fornecedores],
  );

  return (
    <>
      <Row>
        <Col lg={6}>
          <BuscaParceiro
            label="Fornecedor"
            name="busca_parceiro"
            placeholder="Informe a Descrição do Fornecedor"
            register={register}
            control={control}
            isError={!!errors.busca_parceiro}
            customOptions={{
              buscarPor: {
                fornecedor: true,
              },
            }}
            changeSelected={(selected: any) => {
              setValue('busca_parceiro', {
                label: selected.label,
                value: selected.cod_fornecedor ?? selected.value,
              });
              setFormProdutoFornecedor({
                ...formProdutoFornecedor,
                cod_fornecedor: {
                  ...formProdutoFornecedor.cod_fornecedor,
                  value: selected.cod_fornecedor ?? selected.value,
                },
                des_fornecedor: {
                  ...formProdutoFornecedor.des_fornecedor,
                  value: selected.label,
                },
                num_cpf_cnpj: {
                  ...formProdutoFornecedor.num_cpf_cnpj,
                  value: selected.num_cpf_cnpj || '',
                },
              });
              clearErrors('busca_parceiro');
            }}
          />
        </Col>

        <Col lg={2}>
          <ToggleDefault
            labelText="Fabricante?"
            setChecked={flags.flg_fabricante}
            onSwitch={() =>
              ChangeFlags((prev: any) => ({
                flg_fabricante: !prev.flg_fabricante,
              }))
            }
          />
        </Col>

        <Col lg={4} className="d-flex align-items-end gap-2">
          <ConfirmButton
            onClick={handleAddFornecedor}
            disabled={!watch('busca_parceiro')?.value}
          >
            Adicionar
          </ConfirmButton>
          <ClearButton onClick={handleClearProdutoFornecedor}>
            Limpar
          </ClearButton>
        </Col>

        <Col lg={12}>
          <TableContainer>
            <DataGrid
              rows={fornecedores.map((f: any) => ({
                id: f.cod_fornecedor.value,
                cod_fornecedor: f.cod_fornecedor.value,
                num_cpf_cnpj: f.num_cpf_cnpj.value,
                des_fornecedor: f.des_fornecedor.value,
                flg_principal: f.flg_principal.value,
                flg_fabricante: f.flg_fabricante.value,
                unidades: f.Unidades?.unidades || [],
              }))}
              columns={fornecedorColumns}
              disableColumnMenu
              rowsPerPageOptions={[5]}
              disableSelectionOnClick
              pageSize={5}
              localeText={{
                noRowsLabel: 'Nenhum registro encontrado',
                columnMenuLabel: 'Menu',
                columnMenuFilter: 'Filtrar',
                columnMenuHideColumn: 'Esconder',
                columnMenuUnsort: 'Não ordenar',
                columnMenuSortAsc: 'Ordernar ASC',
                columnMenuSortDesc: 'Ordernar DESC',
                columnMenuShowColumns: 'Mostrar columnas',
              }}
            />
          </TableContainer>
        </Col>
      </Row>

      {selectedCodFornecedor && (
        <Row>
          <Col lg={12}>
            <Separator
              labelText={`Unidade/Embalagem - ${
                fornecedores.find(
                  (i: any) =>
                    Number(i.cod_fornecedor.value) === selectedCodFornecedor,
                )?.des_fornecedor.value
              }`}
            />
          </Col>

          <Col lg={3}>
            <InputText
              id="des_referencia"
              label="Referência"
              placeholder="Digite aqui a Referência"
              name="des_referencia"
              maxLength={35}
              isError={!!errors.des_referencia}
              register={register}
              control={control}
              onInput={(ev: ChangeEvent<HTMLInputElement>) => {
                const { value } = ev.target;
                setValue('des_referencia', value.trim());
                clearErrors('des_referencia');
              }}
            />
          </Col>

          <Col lg={3}>
            <InputSelect
              label="Unidade Compra"
              name="des_unidade_compra_fornecedor"
              options={unRequest.data?.unCompras as SelectType[]}
              isError={!!errors.des_unidade_compra_fornecedor}
              register={register}
              control={control}
              changeSelected={(selected: any) => {
                setValue('des_unidade_compra_fornecedor', selected);
              }}
            />
          </Col>

          <Col lg={2}>
            <InputNumber
              label="C/"
              min={1}
              max={99999}
              maxLength={5}
              placeholder=""
              name="qtd_embalagem_compra_fornecedor"
              register={register}
              isError={!!errors.qtd_embalagem_compra_fornecedor}
              onInput={(ev: any) => {
                const value = parseFloat(ev.target.value.slice(0, 5));

                setValue('qtd_embalagem_compra_fornecedor', value);
              }}
            />
          </Col>

          <Col lg={4} className="d-flex align-items-end gap-2">
            <ConfirmButton onClick={handleAddUnidade}>Adicionar</ConfirmButton>
            <ClearButton onClick={handleClearProdFornUN}>Limpar</ClearButton>
          </Col>

          <Col lg={12}>
            <TableContainer>
              <DataGrid
                rows={unidadesDoFornecedorSelecionado}
                columns={unidadeColumns}
                disableColumnMenu
                rowsPerPageOptions={[5]}
                disableSelectionOnClick
                pageSize={5}
                localeText={{
                  noRowsLabel: 'Nenhum registro encontrado',
                  columnMenuLabel: 'Menu',
                  columnMenuFilter: 'Filtrar',
                  columnMenuHideColumn: 'Esconder',
                  columnMenuUnsort: 'Não ordenar',
                  columnMenuSortAsc: 'Ordernar ASC',
                  columnMenuSortDesc: 'Ordernar DESC',
                  columnMenuShowColumns: 'Mostrar columnas',
                }}
              />
            </TableContainer>
          </Col>
        </Row>
      )}
    </>
  );
};
export default AbaFornecedor;
